<template>
    <article class="referral-leakage-audit" :class="{ 'referral-leakage-audit--embed': isEmbed }">
        <header v-if="!isEmbed" class="referral-leakage-audit__header">
            <svg class="referral-leakage-audit__logo svg-logo">
                <use xlink:href="#picture-logo" />
            </svg>
        </header>

        <div v-if="step !== 'results'" class="referral-leakage-audit__modal">
            <button v-if="!isEmbed" type="button" class="referral-leakage-audit__close" aria-label="Close" @click="closeAudit">
                &times;
            </button>

            <p class="referral-leakage-audit__eyebrow">The Referral Leakage Audit</p>

            <div class="referral-leakage-audit__progress-track">
                <div class="referral-leakage-audit__progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>

            <template v-if="step === 'email'">
                <p class="referral-leakage-audit__step-label">Almost done</p>
                <h2 class="referral-leakage-audit__question">Where should we send your score?</h2>
                <p class="referral-leakage-audit__subcopy">Your Referral Leakage Score is ready — enter your email to see it.</p>

                <form class="referral-leakage-audit__form" @submit.prevent="submitAudit">
                    <label class="referral-leakage-audit__field">
                        <span>Name</span>
                        <input v-model.trim="name" type="text" name="name" required autocomplete="name" />
                    </label>
                    <label class="referral-leakage-audit__field">
                        <span>Work email</span>
                        <input v-model.trim="email" type="email" name="email" required autocomplete="email" />
                    </label>
                    <p v-if="submitError" class="referral-leakage-audit__error">{{ submitError }}</p>
                    <button type="submit" class="referral-leakage-audit__cta" :disabled="isSubmitting">
                        {{ isSubmitting ? 'Calculating…' : 'Get My Score' }}
                    </button>
                </form>
                <p class="referral-leakage-audit__fine-print">
                    We'll send your scored results once — no spam, unsubscribe anytime.
                </p>
            </template>

            <template v-else>
                <p class="referral-leakage-audit__step-label">Question {{ currentQuestionIndex + 1 }} of {{ totalQuestions }}</p>
                <h2 class="referral-leakage-audit__question">{{ currentQuestion.text }}</h2>

                <div class="referral-leakage-audit__options">
                    <button
                        v-for="option in currentQuestion.options"
                        :key="option.label"
                        type="button"
                        class="referral-leakage-audit__option"
                        @click="selectAnswer(option.points)"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </template>
        </div>

        <div v-else class="referral-leakage-audit__results">
            <p class="referral-leakage-audit__eyebrow">Your Results</p>
            <p class="referral-leakage-audit__score" :class="`referral-leakage-audit__score--${results.bandKey}`">
                {{ results.score }}
                <span>/100</span>
            </p>
            <p class="referral-leakage-audit__band" :class="`referral-leakage-audit__band--${results.bandKey}`">
                {{ results.bandLabel }}
            </p>
            <p class="referral-leakage-audit__gap">{{ results.gapText }}</p>
            <p class="referral-leakage-audit__benchmark">
                Benchmarked against the referral industry's 25–40% average failure rate.
            </p>
            <a href="#" class="referral-leakage-audit__cta referral-leakage-audit__cta--primary" @click.prevent="onStartFree">
                Fix This Now — Start free with MedMatch Network
            </a>
        </div>
    </article>
</template>

<script>
    import ReferralLeakageAuditAPI from 'api/ReferralLeakageAuditAPI'
    import { AUDIT_QUESTIONS, AUDIT_PARENT_ORIGINS } from '~/constants/referralLeakageAuditQuestions'

    export default {
        data() {
            return {
                questions: AUDIT_QUESTIONS,
                step: 0,
                answers: [],
                name: '',
                email: '',
                results: null,
                isSubmitting: false,
                submitError: null,
            }
        },

        computed: {
            isEmbed() {
                return this.$route.query.embed === '1' || this.$route.query.embed === 'true'
            },

            totalQuestions() {
                return this.questions.length
            },

            currentQuestionIndex() {
                return typeof this.step === 'number' ? this.step : this.totalQuestions - 1
            },

            currentQuestion() {
                return this.questions[this.currentQuestionIndex]
            },

            progressPercent() {
                if (this.step === 'email') {
                    return 100
                }
                if (this.step === 'results') {
                    return 100
                }
                return Math.round(((this.currentQuestionIndex + 1) / this.totalQuestions) * 100)
            },

            utm() {
                const q = this.$route.query
                return {
                    utm_source: q.utm_source || 'direct',
                    utm_medium: q.utm_medium || undefined,
                    utm_campaign: q.utm_campaign || undefined,
                    utm_content: q.utm_content || undefined,
                }
            },

            signupUrl() {
                const base = env('VUE_APP_URL') || ''
                return `${base}/signup/register?utm_source=referral-leakage-audit`
            },
        },

        methods: {
            selectAnswer(points) {
                this.answers[this.currentQuestionIndex] = points

                if (this.currentQuestionIndex < this.totalQuestions - 1) {
                    this.step = this.currentQuestionIndex + 1
                    return
                }

                this.step = 'email'
            },

            async submitAudit() {
                this.submitError = null
                this.isSubmitting = true

                try {
                    const response = await ReferralLeakageAuditAPI.submit({
                        name: this.name,
                        email: this.email,
                        answers: this.answers,
                        utm: this.utm,
                    })

                    this.results = {
                        score: response.score,
                        bandKey: response.bandKey,
                        bandLabel: response.bandLabel,
                        gapText: response.gapText,
                    }
                    this.step = 'results'
                    this.notifyParent('audit_completed')
                } catch (error) {
                    const data = error?.response?.data || error?.response
                    let message = 'Something went wrong. Please try again.'

                    if (data?.data?.message) {
                        message = data.data.message
                    } else if (Array.isArray(data?.data) && data.data[0]?.message) {
                        message = data.data[0].message
                    } else if (data?.message) {
                        message = data.message
                    } else if (error?.message) {
                        message = error.message
                    }

                    this.submitError = message
                } finally {
                    this.isSubmitting = false
                }
            },

            async onStartFree() {
                try {
                    if (this.email) {
                        await ReferralLeakageAuditAPI.notifyStartFree({ email: this.email })
                    }
                } catch (err) {
                    // Best-effort GHL exit tag — still send them to signup.
                }

                const target = window.top || window
                target.location.href = this.signupUrl
            },

            notifyParent(event) {
                if (window.parent === window || !this.isEmbed) {
                    return
                }

                AUDIT_PARENT_ORIGINS.forEach((origin) => {
                    window.parent.postMessage(
                        {
                            type: 'medmatch-audit',
                            event,
                            score: this.results?.score,
                            bandKey: this.results?.bandKey,
                        },
                        origin,
                    )
                })
            },

            closeAudit() {
                if (this.isEmbed) {
                    this.notifyParent('audit_closed')
                }
            },
        },
    }
</script>

<style scoped lang="scss">
    .referral-leakage-audit {
        min-height: 100vh;
        background: #fff;
        color: #001d50;
        font-family: Roboto, sans-serif;

        &--embed {
            min-height: auto;
        }
    }

    .referral-leakage-audit__header {
        padding: 10px 16px;
    }

    .referral-leakage-audit__logo {
        width: 133px;
        height: 70px;
    }

    .referral-leakage-audit__modal,
    .referral-leakage-audit__results {
        max-width: 560px;
        margin: 0 auto;
        padding: 24px 20px 32px;
        position: relative;
    }

    .referral-leakage-audit__results {
        text-align: center;
    }

    .referral-leakage-audit--embed .referral-leakage-audit__modal,
    .referral-leakage-audit--embed .referral-leakage-audit__results {
        padding: 16px 20px 24px;
    }

    .referral-leakage-audit__close {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 36px;
        height: 36px;
        border: 1px solid #d9e2ec;
        border-radius: 50%;
        background: #fff;
        color: #001d50;
        font-size: 22px;
        line-height: 1;
        cursor: pointer;
    }

    .referral-leakage-audit__eyebrow {
        font-family: Poppins, sans-serif;
        font-size: 12px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: #13b3e3;
        margin: 0 0 12px;
    }

    .referral-leakage-audit__progress-track {
        height: 4px;
        background: #e8f7fc;
        border-radius: 999px;
        overflow: hidden;
        margin-bottom: 20px;
    }

    .referral-leakage-audit__progress-fill {
        height: 100%;
        background: #13b3e3;
        transition: width 0.2s ease;
    }

    .referral-leakage-audit__step-label {
        font-size: 14px;
        color: #5a6b7d;
        margin: 0 0 12px;
    }

    .referral-leakage-audit__question {
        font-family: Poppins, sans-serif;
        font-size: 24px;
        line-height: 1.35;
        font-weight: 700;
        margin: 0 0 24px;
        color: #001d50;
    }

    .referral-leakage-audit__subcopy {
        margin: -12px 0 20px;
        color: #5a6b7d;
        line-height: 1.5;
    }

    .referral-leakage-audit__options {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .referral-leakage-audit__option {
        width: 100%;
        padding: 16px 18px;
        border: 1px solid #d9e2ec;
        border-radius: 12px;
        background: #fff;
        color: #001d50;
        font-size: 16px;
        text-align: left;
        cursor: pointer;
        transition: border-color 0.15s ease, background 0.15s ease;

        &:hover {
            border-color: #13b3e3;
            background: #f8fdff;
        }
    }

    .referral-leakage-audit__form {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .referral-leakage-audit__field {
        display: flex;
        flex-direction: column;
        gap: 6px;
        font-size: 14px;
        color: #5a6b7d;

        input {
            padding: 12px 14px;
            border: 1px solid #d9e2ec;
            border-radius: 8px;
            font-size: 16px;
            color: #001d50;
        }
    }

    .referral-leakage-audit__error {
        color: #c0392b;
        font-size: 14px;
        margin: 0;
    }

    .referral-leakage-audit__cta {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        padding: 14px 20px;
        border: none;
        border-radius: 999px;
        background: #13b3e3;
        color: #fff;
        font-family: Poppins, sans-serif;
        font-size: 15px;
        font-weight: 600;
        line-height: 1.35;
        text-align: center;
        text-decoration: none;
        cursor: pointer;

        &:disabled {
            opacity: 0.7;
            cursor: not-allowed;
        }

        &--primary {
            margin-top: 8px;
        }
    }

    .referral-leakage-audit__fine-print {
        margin-top: 14px;
        font-size: 13px;
        color: #5a6b7d;
        line-height: 1.4;
    }

    .referral-leakage-audit__score {
        font-family: Poppins, sans-serif;
        font-size: 56px;
        font-weight: 700;
        margin: 0 0 8px;
        color: #001d50;

        span {
            font-size: 28px;
            color: #5a6b7d;
        }

        &--tight {
            color: #1a936f;

            span {
                color: #5a9e86;
            }
        }

        &--leaking {
            color: #d97706;

            span {
                color: #c4923a;
            }
        }

        &--critical {
            color: #c0392b;

            span {
                color: #c96a60;
            }
        }
    }

    .referral-leakage-audit__band {
        font-family: Poppins, sans-serif;
        font-size: 22px;
        font-weight: 600;
        margin: 0 0 16px;

        &--tight {
            color: #1a936f;
        }

        &--leaking {
            color: #d97706;
        }

        &--critical {
            color: #c0392b;
        }
    }

    .referral-leakage-audit--embed .referral-leakage-audit__score {
        font-size: 48px;

        span {
            font-size: 24px;
        }
    }

    .referral-leakage-audit--embed .referral-leakage-audit__band {
        font-size: 20px;
    }

    .referral-leakage-audit--embed .referral-leakage-audit__cta--primary {
        font-size: 14px;
        padding: 13px 16px;
    }

    .referral-leakage-audit__gap,
    .referral-leakage-audit__benchmark {
        line-height: 1.55;
        color: #334e68;
        margin: 0 0 16px;
    }

    .referral-leakage-audit__benchmark {
        font-size: 14px;
        color: #5a6b7d;
    }
</style>
